import type React from 'react';

import { useLocation } from 'react-router';

import { ThemeProvider } from '@change/ds-core-ui/ThemeProvider';

import { isDarkModeExcludedPath } from '#src/shared/darkMode';

type Props = {
	darkModeEnabled: boolean;
	children: React.ReactNode;
};

/**
 * The app's root ThemeProvider. `auto` tells design-system components, such as `@change/ds-illustrations`, that dark
 * mode is allowed on this page and the OS theme decides; `light` rules it out. It uses the same inputs as
 * DarkModeExclusion (the FCM_DARK_MODE_ENABLED kill switch and the excluded paths), so it can't disagree with the
 * `.dark` class, and the server renders the same mode as the first client render.
 *
 * A separate component, not part of App, so only it re-renders when the location changes.
 */
export const RootThemeProvider: React.FC<Props> = ({ darkModeEnabled, children }) => {
	const { pathname } = useLocation();

	return (
		<ThemeProvider mode={darkModeEnabled && !isDarkModeExcludedPath(pathname) ? 'auto' : 'light'}>
			{children}
		</ThemeProvider>
	);
};
